import React from 'react';

import type { ImageVideoHeroBannerProps } from 'Common/util/cmsComposer/HeroBanner';

import Main from 'Elements/Main';
import Section from 'Elements/Section';
import ArrowButton, { ArrowLinkButtonProps } from 'Elements/Button/Arrow';

import Grid, { Col } from 'Components/Grid';
import UpcomingEvents, { EventsUpcomingProps } from 'Components/Events/Upcoming';
import Breadcrumbs, { BreadcrumbsProps } from 'Components/Breadcrumbs';
import SupportBlockLink, { SupportBlockLinkProps } from 'Components/SupportBlock/Link';

import VideoHeroBanner from 'Modules/HeroBanner/MakeWaves/Video';
import ImageHeroBanner from 'Modules/HeroBanner/MakeWaves/Image';
import EventAccordions, {
    EventAccordionsSectionOpenDays
} from 'Modules/OpenDays/Section/EventAccordions';
import CampusTour, { CampusTourProps } from 'Modules/OpenDays/Section/CampusTour';
import OnDemand, { OnDemandProps } from 'Modules/OpenDays/Section/OnDemand';
import ContactUs, { ContactUsProps } from 'Modules/OpenDays/Section/ContactUs';
import TefAward, { PromoPointTefAwardProps } from 'Modules/PromoPoint/TefAward';
import ImageFade, { ImageFadeBannerProps } from 'Modules/Banner/ImageFade';

import {
    SideBox,
    SideBoxText,
    SideBoxDate,
    UpcomingEvent,
    TextCol,
    EventCol
} from './Events.styled';

export interface Props {
    breadcrumbs: BreadcrumbsProps;
    heroBanner: ImageVideoHeroBannerProps;
    eventIntroText: React.ReactNode;
    nextEvent?: {
        text: string;
        date: string;
        link: ArrowLinkButtonProps;
    };
    voucher: SupportBlockLinkProps;
    eventAccordions: EventAccordionsSectionOpenDays;
    tefAward: PromoPointTefAwardProps;
    imageFadeBanner: ImageFadeBannerProps;
    campusTour: CampusTourProps;
    onDemand: OnDemandProps;
    upcomingEvents: EventsUpcomingProps;
    contactUs: ContactUsProps;
}

const Events: React.FC<Props> = props => {
    const {
        breadcrumbs,
        heroBanner,
        eventIntroText,
        nextEvent,
        voucher,
        eventAccordions,
        tefAward,
        imageFadeBanner,
        campusTour,
        onDemand,
        upcomingEvents,
        contactUs
    } = props;

    return (
        <Main>
            <Breadcrumbs {...breadcrumbs} />
            {heroBanner.video && <VideoHeroBanner {...heroBanner.video} />}
            {heroBanner.image && <ImageHeroBanner {...heroBanner.image} />}
            <Section background paddingTop={1.5} id="event-intro">
                <Grid>
                    <TextCol m={6} l={[1, 7]}>
                        {eventIntroText}
                    </TextCol>
                    <EventCol m={6} l={[8, 12]}>
                        <SideBox>
                            {nextEvent && (
                                <UpcomingEvent id="next-event">
                                    <SideBoxText>{nextEvent.text}</SideBoxText>
                                    <SideBoxDate>{nextEvent.date}</SideBoxDate>
                                    <ArrowButton {...nextEvent.link} />
                                </UpcomingEvent>
                            )}
                            <SupportBlockLink {...voucher} />
                        </SideBox>
                    </EventCol>
                </Grid>
            </Section>
            <Section background id="event-accordions">
                <Grid>
                    <Col>
                        <EventAccordions {...eventAccordions} />
                    </Col>
                </Grid>
            </Section>
            <Section background id="tef-award">
                <Grid>
                    <Col>
                        <TefAward {...tefAward} />
                    </Col>
                </Grid>
            </Section>
            <ImageFade {...imageFadeBanner} />
            <OnDemand {...onDemand} />
            {upcomingEvents.events.length !== 0 && (
                <Section paddingTop="none" id="other-events" background>
                    <Grid>
                        <Col l={[2, 12]}>
                            <UpcomingEvents {...upcomingEvents} />
                        </Col>
                    </Grid>
                </Section>
            )}
            <ContactUs {...contactUs} />
            <CampusTour {...campusTour} />
        </Main>
    );
};

export default Events;
